
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { signIn, signUp } from "@/lib/auth";
import { translateAuthError, emailConfirmationMessage } from "@/lib/auth-errors";
import { validatePassword } from "@/lib/password-validation";
import { apiFetch } from "@/lib/api-client";
import { useEmailValidation } from "./useEmailValidation";
import { useInteractionGuards } from "./useInteractionGuards";

export const useAuthForm = (onSuccess: () => void) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showConfirmMessage, setShowConfirmMessage] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const { toast } = useToast();
  const { validateEmail } = useEmailValidation();
  const {
    isTooFast,
    hasLowInteractionCount,
    isRateLimited,
    trackUserInteraction,
    trackKeyPress,
    resetAll,
    registerAttempt
  } = useInteractionGuards();

  const toggleFormMode = () => {
    setIsLogin((v) => !v);
    setShowConfirmMessage(false);
    setPasswordErrors([]);
    setEmailError(null);
    resetAll();
  };

  const validateAndSetEmail = (newEmail: string) => {
    setEmail(newEmail);
    const validation = validateEmail(newEmail);
    setEmailError(validation.error);
  };

  const validateAndSetPassword = (newPassword: string) => {
    setPassword(newPassword);
    if (!isLogin) {
      const validation = validatePassword(newPassword);
      setPasswordErrors(validation.errors);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      toast({
        title: "Vale e-posti formaat",
        description: emailValidation.error,
        variant: "destructive",
      });
      setEmailError(emailValidation.error);
      return;
    }

    try {
      if (isTooFast()) {
        console.log("Form filled too quickly - likely bot");
        toast({
          title: "Viga",
          description: "Liiga kiire sisestamine. Palun proovi uuesti.",
          variant: "destructive",
        });
        return;
      }

      if (hasLowInteractionCount()) {
        console.log("Low interaction count - suspicious");
        toast({
          title: "Viga",
          description: "Palun täida vorm põhjalikumalt.",
          variant: "destructive",
        });
        return;
      }

      if (isRateLimited()) {
        console.log("Rate limited - too many attempts");
        toast({
          title: "Liiga palju katseid",
          description: "Palun oota enne uut katset.",
          variant: "destructive",
        });
        return;
      }
      
      registerAttempt();

      if (!isLogin) {
        const validation = validatePassword(password);
        setPasswordErrors(validation.errors);
        if (!validation.isValid) {
          toast({
            title: "Nõrk parool",
            description: "Palun vali tugevam parool, järgides kõiki nõudeid.",
            variant: "destructive",
          });
          return;
        }
      }
      setLoading(true);
      if (isLogin) {
        const { error } = await signIn(email, password);
        if (error) throw error;

        toast({
          title: "Edukalt sisse logitud",
          description: "Nüüd saad osta kursuse",
        });
        onSuccess();
      } else {
        if (!fullName.trim()) {
          throw new Error("Palun sisesta oma täisnimi");
        }

        // Generate redirect URL for checkout flow
        const currentPath = window.location.pathname;
        const redirectUrl = `${window.location.origin}/email-confirm-success?redirect_to=${encodeURIComponent(currentPath)}`;
        
        console.log("Sign up with redirect URL:", redirectUrl);
        
        const { error } = await signUp(email, password, fullName, {
          redirectTo: redirectUrl
        });
        
        if (error) throw error;

        try {
          await apiFetch('/api/mailerlite/subscribe', {
            method: 'POST',
            body: JSON.stringify({ email, name: fullName }),
          });
        } catch (mailerliteError) {
          console.error("Failed to add user to MailerLite:", mailerliteError);
        }

        setShowConfirmMessage(true);
        toast({
          title: "Konto edukalt loodud",
          description: emailConfirmationMessage,
        });
        setIsLogin(true);
      }
    } catch (error: any) {
      console.error("Authentication error:", error);
      toast({
        title: "Autentimine ebaõnnestus",
        description: translateAuthError(error.message),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    isLogin,
    email,
    setEmail: validateAndSetEmail,
    emailError,
    password,
    setPassword: validateAndSetPassword,
    fullName,
    setFullName,
    showPassword,
    setShowPassword,
    loading,
    showConfirmMessage,
    passwordErrors,
    trackUserInteraction,
    trackKeyPress,
    toggleFormMode,
    handleSubmit,
  };
};
